import type { EvidenceKind } from "@walkie/protocol"
import { useState } from "react"
import { oneLine } from "../lib/messages"
import { resolveRequest, withdrawMessage } from "../store/actions"
import { setUi, useStore } from "../store/store"
import { KindBadge, useDismiss } from "./ui"

// Mirrors evidenceKinds in @walkie/protocol (a runtime value there; the web app imports types only).
const EVIDENCE: readonly EvidenceKind[] = [
	"pr",
	"commit",
	"check",
	"artifact",
	"message",
	"note",
]

const KEYS: [string, string[]][] = [
	["Search / jump", ["⌘K"]],
	["Search / jump", ["/"]],
	["Next / previous message", ["j", "k"]],
	["Reply in thread", ["r"]],
	["Open thread", ["↵"]],
	["Claim selected request", ["c"]],
	["Resolve selected request", ["e"]],
	["Withdraw selected message (yours)", ["w"]],
	["Show / hide GitHub events", ["g", "h"]],
	["Focus composer", ["n"]],
	["Jump to rooms", ["g", "r"]],
	["Jump to agents", ["g", "a"]],
	["Toggle rooms rail", ["["]],
	["Toggle agents panel", ["]"]],
	["Rename focused agent", ["F2"]],
	["Close / back", ["esc"]],
	["This sheet", ["?"]],
]

export function ShortcutSheet() {
	const close = () => setUi({ shortcuts: false })
	useDismiss(close)
	return (
		<>
			<div className="scrim" onMouseDown={close} />
			<div className="dialog wide" role="dialog" aria-label="Keyboard shortcuts">
				<div className="dialog-h">
					Keyboard shortcuts
					<button className="icon-btn" aria-label="Close" onClick={close} autoFocus>
						×
					</button>
				</div>
				<div className="keys">
					{KEYS.map(([label, keys], i) => (
						<div key={i} className="key-row">
							<span>{label}</span>
							<span>
								{keys.map(k => (
									<kbd key={k}>{k}</kbd>
								))}
							</span>
						</div>
					))}
				</div>
			</div>
		</>
	)
}

export function ResolveDialog({ mid }: { mid: string }) {
	const m = useStore(s => s.messages[mid])
	const close = () => setUi({ resolveFor: null })
	useDismiss(close)
	const [resolution, setResolution] = useState("")
	const [kind, setKind] = useState<EvidenceKind>("pr")
	const [ref, setRef] = useState("")
	const [busy, setBusy] = useState(false)
	if (!m?.request) return null
	const submit = async (cancel = false) => {
		setBusy(true)
		const ok = await resolveRequest(mid, {
			resolution: resolution.trim() || undefined,
			evidence: ref.trim() ? [{ kind, ref: ref.trim() }] : [],
			cancel: cancel || undefined,
		})
		setBusy(false)
		if (ok) close()
	}
	return (
		<>
			<div className="scrim" onMouseDown={close} />
			<form
				className="dialog"
				role="dialog"
				aria-label="Resolve request"
				onSubmit={e => {
					e.preventDefault()
					void submit()
				}}
			>
				<div className="dialog-h">
					<KindBadge kind={m.kind} />
					<span className="ell">Resolve: {m.title ?? m.body.split("\n")[0]}</span>
					<button type="button" className="icon-btn" aria-label="Close" onClick={close}>
						×
					</button>
				</div>
				<label className="field">
					Resolution
					<textarea
						className="textarea"
						rows={3}
						autoFocus
						value={resolution}
						onChange={e => setResolution(e.target.value)}
						placeholder="What was done, or the answer"
						onKeyDown={e => {
							if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) void submit()
						}}
					/>
				</label>
				<div className="row" style={{ gap: 6 }}>
					<label className="field" style={{ width: 110 }}>
						Evidence
						<select
							className="select"
							value={kind}
							onChange={e => setKind(e.target.value as EvidenceKind)}
						>
							{EVIDENCE.map(k => (
								<option key={k} value={k}>
									{k}
								</option>
							))}
						</select>
					</label>
					<label className="field grow">
						Reference
						<input
							className="input mono"
							value={ref}
							onChange={e => setRef(e.target.value)}
							placeholder={
								kind === "pr"
									? "owner/repo#123"
									: kind === "commit"
										? "owner/repo@sha"
										: "link or note"
							}
						/>
					</label>
				</div>
				<div className="dialog-actions">
					<button
						type="button"
						className="btn btn-ghost btn-danger"
						disabled={busy}
						onClick={() => void submit(true)}
					>
						Cancel request
					</button>
					<span className="grow" />
					<button type="button" className="btn" onClick={close}>
						Back
					</button>
					<button type="submit" className="btn btn-primary" disabled={busy}>
						Resolve{" "}
						<kbd
							style={{
								background: "transparent",
								color: "inherit",
								borderColor: "rgb(0 0 0 / .25)",
							}}
						>
							⌘↵
						</kbd>
					</button>
				</div>
			</form>
		</>
	)
}

export function WithdrawDialog({ mid }: { mid: string }) {
	const m = useStore(s => s.messages[mid])
	const me = useStore(s => s.me?.user.id)
	const close = () => setUi({ withdrawFor: null })
	useDismiss(close)
	const [busy, setBusy] = useState(false)
	if (!m) return null
	const openReq =
		!!m.request && (m.request.status === "open" || m.request.status === "claimed")
	const notMine = !(m.author.type === "user" && m.author.id === me)
	const go = async () => {
		setBusy(true)
		const ok = await withdrawMessage(mid)
		setBusy(false)
		if (ok) close()
	}
	return (
		<>
			<div className="scrim" onMouseDown={close} />
			<div
				className="dialog"
				role="alertdialog"
				aria-modal="true"
				aria-labelledby="wd-h"
				aria-describedby="wd-d"
			>
				<div className="dialog-h" id="wd-h">
					<KindBadge kind={m.kind} />
					<span className="ell">Withdraw: {oneLine(m, 80) || "message"}</span>
					<button type="button" className="icon-btn" aria-label="Close" onClick={close}>
						×
					</button>
				</div>
				<div id="wd-d" className="dim" style={{ lineHeight: 1.45 }}>
					The text, title and refs are removed for everyone, including agents that
					read it later. A tombstone keeps its place and its thread.
					{openReq && " The open request is cancelled."}
					{notMine && ` You are withdrawing @${m.author.handle}'s message as an admin.`}{" "}
					This cannot be undone.
				</div>
				<div className="dialog-actions">
					<span className="grow" />
					<button type="button" className="btn" onClick={close} autoFocus>
						Keep it
					</button>
					<button
						type="button"
						className="btn btn-danger"
						disabled={busy}
						onClick={() => void go()}
					>
						Withdraw
					</button>
				</div>
			</div>
		</>
	)
}
